import { useState, useEffect, useRef } from 'react';
import NavigationMenu from './NavigationMenu';
import Footer from './Footer';
import { Box } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useApp } from '@context/AppContext';
import { PublicRoutes } from '@enums/publicRouteEnums';
import WalnutWalkthroughModal from '@components/walkthrough/WalnutWalkthroughModal';
import AnnouncementBanner from '@components/layout/AnnouncementBanner';
import {
  useFetchAnnouncementStatusApi,
  useSetAnnouncementHiddenApi,
} from '@hooks/api/useProfileApi';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@enums/queryKeys';

export default function PageLayout({
  children,
}: {
  readonly children: React.ReactNode;
}) {
  const { isAuthorized } = useApp();
  const [showModal, setShowModal] = useState(false);
  const hasShownModal = useRef(false);
  const queryClient = useQueryClient();

  const { data: announcementStatus } =
    useFetchAnnouncementStatusApi(isAuthorized);
  const { mutateAsync: setAnnouncementHidden } = useSetAnnouncementHiddenApi();

  useEffect(() => {
    // Only show the modal once per session, and only if the user hasn't hidden it
    if (
      isAuthorized &&
      announcementStatus &&
      !announcementStatus.hasHiddenNewPortalAnnouncement &&
      !hasShownModal.current
    ) {
      hasShownModal.current = true;
      setShowModal(true);
    }
  }, [isAuthorized, announcementStatus]);

  const handleDontShowAgain = async (checked: boolean) => {
    if (checked) {
      await setAnnouncementHidden();
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.ANNOUNCEMENT_STATUS],
      });
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  if (!isAuthorized) {
    return <Navigate to={PublicRoutes.LOGIN} replace />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        width: '100%',
        overflow: 'auto',
      }}
    >
      <AnnouncementBanner />
      <NavigationMenu />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          pt: 'calc(64px + var(--announcement-offset, 0px))',
        }}
      >
        <Box sx={{ width: '100%', flexGrow: 1 }}>{children}</Box>
      </Box>

      <Footer />

      {isAuthorized && (
        <WalnutWalkthroughModal
          open={showModal}
          onClose={handleCloseModal}
          onDontShowAgain={handleDontShowAgain}
        />
      )}
    </Box>
  );
}
